<template>
  <div class="h-full pb-8 flex flex-col justify-between">
    <div>
      <span class="font-bold text-gray-dark text-xl">Wait for customer input</span>

      <div class="flex flex-col justify-between items-center mt-6">
        <div class="flex">
          <div class="flex">
            <vs-checkbox :value="canSaveUserInput"
                         @change="toggleSaveUserInput"
                         :color="checkboxFocusColor"
                         class="ml-0"/>
            <div class="text-gray-dark flex items-center">Save input as:</div>
          </div>
          <base-input :disabled="!canSaveUserInput"
                      v-model="contextKey"
                      class="text-gray-dark flex-1 h-10 ml-3"
                      :class="{'border-focus-error focus:border-focus-error': isContextKeyError}"
                      :color="contextKeyInputFocusColor"
                      placeholder="custom-name"
                      variant="editor-soft"
                      :maxlength="30"
                      ref="contextKeyInput"
          />
        </div>
        <div v-if="isContextKeyError"
             class="text-focus-error text-xs mt-1 text-left w-full"
        >
          {{ errorDescription }}
        </div>
      </div>

      <div class="text-gray-dark mt-4 text-sm">
        You can save customer input in a <strong>context</strong> to access it later on,
        e.g. print what the customer said or to send data to your server.
      </div>

      <div class="text-gray-dark mt-4 text-sm">
        <div class="flex items-center">
          <span>Once saved, access it via <code>${context.{{ contextKey || 'custom-name' }}}</code></span>

          <vx-tooltip v-if="!!contextKey"
                      ref="copyIconTooltip"
                      class="flex items-center"
                      :text="isCopied ? 'Context key was copied to your clipboard!' : 'Copy context key'"
          >
            <copy-icon class="h-5 ml-1 text-gray-dark hover:text-primary-focus cursor-pointer"
                      :is-copied="isCopied"
                      @click.native="copyContextKey"/>
          </vx-tooltip>
        </div>
        <br />
        Example: Your email is <code>${context.{{ contextKey || 'email' }}}</code>
      </div>

      <wait-for-input-editor-validation :validation-params.sync="validationParams"
                                        class="mt-4"
      />
    </div>

    <base-button variant="bordered-error" class="w-full mt-8" @click="removeWaitForInput">
      Remove wait for customer input
    </base-button>
  </div>
</template>

<script>
import conversationModule from '@/store/modules/conversation.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import templateModule from '@/store/modules/template.module'
import tailwind from '../../../tailwind.config'
import answerController from '@/controllers/answer.controller'
import templateController from '@/controllers/template.controller'
import BotStateUtils from '@/utils/bot-state.utils'
import WaitForInputEditorValidation from '@/components/editor/WaitForInputEditorValidation.vue'
import CharacterCounter from '@/components/editor/message/CharacterCounter.vue'
import { VALIDATION_PARAMS, VALIDATION_PATTERNS } from '@/utils/validation.constants'
import CopyIcon from '@/assets/images/icons/CopyIcon.vue'
import previewModule from '@/store/modules/preview.module'

export default {
  name: 'WaitForInputEditor',

  components: {
    CharacterCounter,
    WaitForInputEditorValidation,
    CopyIcon
  },

  data() {
    return {
      isCopied: false
    }
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    stateTree: chatBubbleModule.stateTree,
    errorDescription: previewModule.errorDescription,

    isContextKeyError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription.startsWith('Context key')
    },

    checkboxFocusColor() {
      return tailwind.theme.colors.focus
    },

    contextKeyInputFocusColor() {
      return this.isContextKeyError ?
        tailwind.theme.colors['focus-error'] :
        tailwind.theme.colors.focus
    },

    stateId() {
      return this.bubbleSelected.stateId
    },

    nextStateId() {
      return this.bubbleSelected.nextStateId
    },

    validationActionId() {
      return this.bubbleSelected.validationActionId
    },

    contextStateId() {
      return this.bubbleSelected.contextStateId
    },

    canSaveUserInput() {
      return !!this.contextStateId
    },

    previewController() {
      return templateModule.isTemplateEditor() ? templateController : answerController
    },

    validationParams: {
      get() {
        if (!this.validationActionId) return VALIDATION_PARAMS.none

        return conversationModule.getAction(this.stateId, this.validationActionId)?.params
      },
      set(newValidationParams) {
        const oldPattern = this.validationParams.pattern
        const newPattern = newValidationParams.pattern
        const patternChanged = oldPattern !== newPattern
        if (patternChanged) this.contextKey = newPattern

        if (newPattern === VALIDATION_PATTERNS.NONE) {
          conversationModule.removeAction(this.stateId, this.validationActionId)
          chatBubbleModule.selectWaitForInput(this.stateId, this.nextStateId, null, this.contextStateId)
        } else if (oldPattern === VALIDATION_PATTERNS.NONE) {
          const actionId = this.previewController.addValidationAction(this.stateId, VALIDATION_PARAMS[newPattern])
          chatBubbleModule.selectWaitForInput(this.stateId, this.nextStateId, actionId, this.contextStateId)
        } else {
          const validationParams = patternChanged ? VALIDATION_PARAMS[newPattern] : newValidationParams
          conversationModule.setValidationAction(this.validationActionId, validationParams)
        }
      }
    },

    contextKey: {
      get() {
        return conversationModule.getState(this.contextStateId)?.contextKey || ''
      },
      set(newContextKey) {
        if (!this.contextStateId) return

        conversationModule.renameContextKey(this.contextStateId, newContextKey)
      }
    }
  },

  methods: {
    toggleSaveUserInput() {
      const canSaveUserInput = !this.canSaveUserInput
      if (!canSaveUserInput) {
        this.previewController.removeSaveContextState(this.stateId, this.nextStateId, this.contextStateId, templateModule.getTemplateId())
        chatBubbleModule.selectWaitForInput(this.stateId, this.nextStateId, this.validationActionId)
        return
      }

      const contextStateId = BotStateUtils.generateId(this.stateId)
      this.previewController.addSaveContextState(this.stateId, this.nextStateId, contextStateId, this.contextKey, templateModule.getTemplateId())
      chatBubbleModule.selectWaitForInput(this.stateId, this.nextStateId, this.validationActionId, contextStateId)
      if (this.validationParams.pattern !== VALIDATION_PATTERNS.NONE) this.contextKey = this.validationParams.pattern
    },

    removeWaitForInput() {
      this.previewController.removeWaitForInput(this.stateTree, this.bubbleSelected, templateModule.getTemplateId())
    },

    async copyContextKey() {
      this.isCopied = true
      await this.$copyText('${' + `context.${ this.contextKey }` + '}')
      this.$refs.copyIconTooltip.mouseenterx()
      await this.sleep(2000)
      this.isCopied = false
      this.$refs.copyIconTooltip.mouseleavex()
    }
  },

  watch: {
    isContextKeyError: {
      immediate: false,
      handler(isContextKeyError) {
        if (isContextKeyError) this.$refs.contextKeyInput.focus()
      }
    }
  },

  mounted() {
    if (this.isContextKeyError) this.$refs.contextKeyInput.focus()
  }
}
</script>

<style scoped lang="scss">
code {
  @apply bg-soft rounded;
}
</style>
